import { Dispatch, FC, SetStateAction } from 'react'
import {
	Control,
	FormState,
	UseFormGetValues,
	UseFormReset,
	UseFormSetValue,
	UseFormTrigger,
	UseFormWatch
} from 'react-hook-form'

import { IOption } from '@/types/selectOption.interface'
import { IShotlist } from '@/types/shotlist.interface'
import { ILanguage } from '@/types/task.interface'
import {
	IPlatform,
	ISelectedPlatform,
	IUploaderForm,
	TPlatformLabel,
	TPlatformValue
} from '@/types/uploadForm.interface'

import { useModalUploadFormController } from '../hooks/useModalUploadFormController'

import FormContent from './FormContent'
import { SourceTabs } from './SourceTabs'

interface IModalUploadFormProps {
	uploaderFormControl: Control<IUploaderForm>
	uploaderFormGetValues: UseFormGetValues<IUploaderForm>
	uploaderFormWatch: UseFormWatch<IUploaderForm>
	uploaderFormSetValue: UseFormSetValue<IUploaderForm>
	formState: FormState<IUploaderForm>
	uploaderFormReset: UseFormReset<IUploaderForm>
	trigger: UseFormTrigger<IUploaderForm>
	taskId: string
	isUploaded: boolean
	setIsUploaded: (isUploaded: boolean) => void
	error?: boolean
	onSuccess?: () => void
	setIsFormUploading: (value: boolean) => void
	isSuccess?: boolean
	hasUploadedVideo?: boolean
	firstPlatformLabel?: TPlatformLabel
	platformList?: IPlatform[]
	languages?: ILanguage[]
	isUploading?: boolean
	isTranscribing?: boolean
	handleTranscribe?: (value: string) => void
	handleTranslate?: (value: string) => void
	handleUploadSrt?: (value: File) => void
	selectedTranscribingLang?: ILanguage | null
	setSelectedTranscribingLang?: Dispatch<SetStateAction<ILanguage | null>>
	isTranslating?: boolean
	selectedPlatform?: TPlatformValue | null
	setSelectedPlatform?: Dispatch<SetStateAction<TPlatformValue | null>>
	selectedPlatformList?: ISelectedPlatform[]
	setSelectedPlatformList?: Dispatch<SetStateAction<ISelectedPlatform[]>>
	availableSourceLanguages?: IOption[]
	setAvailableSourceLanguages?: Dispatch<SetStateAction<IOption[]>>
	refetchShotlist?: () => Promise<unknown>
	shotlist?: IShotlist | null
	pendingSrtFileName?: string
	handleDeleteTranslation?: () => void
	onStopTranscription?: () => void
	handleAutoFill: () => void
	sourceAutoFillLang: TPlatformValue | ''
	setSourceAutoFillLang: Dispatch<SetStateAction<TPlatformValue | ''>>
}

const ModalUploadForm: FC<IModalUploadFormProps> = ({
	uploaderFormControl,
	uploaderFormGetValues,
	uploaderFormWatch,
	uploaderFormSetValue,
	formState,
	uploaderFormReset,
	trigger,
	taskId,
	onSuccess,
	isUploaded,
	setIsUploaded,
	setIsFormUploading,
	isSuccess,
	hasUploadedVideo,
	firstPlatformLabel,
	platformList,
	languages,
	isUploading,
	isTranscribing,
	handleTranscribe,
	handleTranslate,
	handleUploadSrt,
	selectedTranscribingLang,
	setSelectedTranscribingLang,
	isTranslating,
	selectedPlatform,
	setSelectedPlatform,
	selectedPlatformList,
	setSelectedPlatformList,
	availableSourceLanguages,
	setAvailableSourceLanguages,
	refetchShotlist,
	shotlist,
	pendingSrtFileName,
	handleDeleteTranslation,
	onStopTranscription,
	handleAutoFill,
	sourceAutoFillLang,
	setSourceAutoFillLang
}) => {
	const {
		tagsOptions,
		categoriesOptions,
		regionsOptions,
		countryOptions,
		handleFieldChange,
		onSubmit,
		handleAddPlatform,
		handleDeletePlatform,
		handleCloseErrorMessage,
		uploadButtonDisabled
	} = useModalUploadFormController({
		uploaderFormGetValues,
		uploaderFormWatch,
		uploaderFormSetValue,
		uploaderFormReset,
		taskId,
		onSuccess,
		setIsUploaded,
		setIsFormUploading,
		firstPlatformLabel,
		platformList,
		selectedPlatform,
		setSelectedPlatform,
		selectedPlatformList,
		setSelectedPlatformList,
		setSelectedTranscribingLang,
		setAvailableSourceLanguages,
		refetchShotlist
	})

	const renderFormContent = () => (
		<div className='flex gap-[60px] pt-5'>
			<FormContent
				control={uploaderFormControl}
				handleFieldChange={handleFieldChange}
				countryOptions={countryOptions}
				taskId={taskId}
				selectedPlatform={selectedPlatform}
				tagsOptions={tagsOptions}
				categoriesOptions={categoriesOptions}
				regionsOptions={regionsOptions}
				onSubmit={onSubmit}
				isUploaded={isUploaded}
				formState={formState}
				trigger={trigger}
				isSuccess={isSuccess}
				hasUploadedVideo={hasUploadedVideo}
				deletePlatform={handleDeletePlatform}
				platformList={platformList}
				languages={languages}
				isUploading={isUploading}
				isTranscribing={isTranscribing}
				handleTranscribe={handleTranscribe}
				handleTranslate={handleTranslate}
				handleUploadSrt={handleUploadSrt}
				selectedTranscribingLang={selectedTranscribingLang}
				setSelectedTranscribingLang={setSelectedTranscribingLang}
				isTranslating={isTranslating}
				selectedPlatformList={selectedPlatformList}
				availableSourceLanguages={availableSourceLanguages}
				shotlist={shotlist}
				pendingSrtFileName={pendingSrtFileName}
				handleCloseErrorMessage={handleCloseErrorMessage}
				handleDeleteTranslation={handleDeleteTranslation}
				onStopTranscription={onStopTranscription}
				handleAutoFill={handleAutoFill}
				sourceAutoFillLang={sourceAutoFillLang}
				setSourceAutoFillLang={setSourceAutoFillLang}
				isProcessing={uploadButtonDisabled}
			/>
		</div>
	)

	return (
		<div className='h-full overflow-y-auto'>
			<SourceTabs
				selectedPlatform={selectedPlatform}
				onPlatformChange={setSelectedPlatform}
				formContent={renderFormContent()}
				isUploaded={isUploaded}
				platformList={platformList}
				selectedPlatformList={selectedPlatformList}
				addPlatform={handleAddPlatform}
			/>
		</div>
	)
}

export default ModalUploadForm
